import axios from 'axios';
import Logger from '../functions/logging';

import settings from '../libs/config';

const endpointFollowUp = settings.follow_up_agent_host + "/follow-up";

//:NOTE:: middle of three deadlines. The agent's model budget is 6s and the player allows
//itself 9s, so this sits between them: giving up before the service does means the failure
//is never reported — no 503, no log — and a study quietly returns fewer probed answers.
//Keep the ordering agent 6000 < relay 8000 < player 9000.
const FOLLOW_UP_TIMEOUT_MS = 8000;

//:NOTE:: every path resolves. A caller must never be left waiting on this, so a failure of
//any kind — validation, unavailable service, network, timeout — comes back as the same
//"could not reach an outcome" shape the service itself uses for a 503.
const failedOutcome = { outcome: 'failed', followUp: null, meta: null };

/**
 * Ask the agent for a follow-up question.
 *
 * The service is stateless: the whole sequence so far is passed on every call. It answers
 * 200 with an outcome of `generated` or `declined`, 400 if the payload is invalid, or 503
 * if it could not reach an outcome at all. Only `generated` carries a question.
 */
export const requestFollowUp = async (payload) => {
	//:NOTE:: studyKey, sequenceKey and respondentKey are required and must be 1-200 chars.
	//A payload missing one is rejected with a 400 before any model call, so catch it here
	//rather than spending a request to be told. Behaviour is the same either way — the
	//respondent moves on with no follow-up — but this keeps the cause in our own logs
	//instead of it reading as an agent fault.
	const missing = ['studyKey', 'sequenceKey', 'respondentKey'].filter((key) => {
		const value = payload && payload[key];
		return typeof value !== 'string' || value.length < 1 || value.length > 200;
	});

	if (missing.length) {
		Logger.log('followUp', 'requestFollowUp()', 'not calling the agent, invalid keys', missing.join(', '));
		return failedOutcome;
	}

	try {
		const response = await axios.post(endpointFollowUp, payload, {
			timeout: FOLLOW_UP_TIMEOUT_MS,
			headers: { 'Content-Type': 'application/json' }
		});

		const result = response && response.data;

		if (!result || typeof result.outcome !== 'string') {
			Logger.log('followUp', 'requestFollowUp()', 'unrecognised response shape', result);
			return failedOutcome;
		}

		return {
			outcome: result.outcome,
			followUp: result.followUp || null,
			meta: result.meta || null
		};
	} catch (error) {
		//:NOTE:: the respondent-facing behaviour is identical on every path here — no
		//follow-up, carry on — so these are separated only in the log, where the difference
		//decides who has a problem to fix.
		//
		//A 400 means we sent something the agent could not read, which is a bug on our side.
		//A 429 means the respondent reached one of the agent's own ceilings, which is the
		//agent working as intended rather than either side failing; logging it as an outage
		//would inflate the failure rate with calls that were answered correctly. Anything
		//else is the agent being unreachable or too slow, which is the only one of the three
		//worth alarming on.
		const status = error && error.response ? error.response.status : null;
		let cause = 'agent unreachable or timed out';

		if (status === 400) {
			cause = 'payload rejected by the agent';
		} else if (status === 429) {
			cause = 'respondent reached an agent guardrail, no follow-up spent';
		}

		Logger.log('followUp', 'requestFollowUp()', cause, status, error && error.message);

		return failedOutcome;
	}
};



// WEBPACK FOOTER //
// ./src/api/followUp.js